import * as React from "react";
import { ShieldCheck, Mail, FileText } from "lucide-react";
import { Separator } from "@/components/ui/separator";
export default function PrivacyPolicy() {
  const contactEmail = "sales@mechanicalbullshop.com";
  return <section id="privacypolicycontent" data-nav="light" className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Block */}
        <header className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted border border-border text-xs font-mono tracking-wide uppercase text-muted-foreground mb-4">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Legal Documentation</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground uppercase mb-4">
            Privacy Policy
          </h1>

          <p className="text-sm font-mono text-muted-foreground mb-8">
            Last updated: September 18, 2026
          </p>

          <div className="space-y-4 text-base md:text-lg leading-relaxed text-foreground border-l-2 border-primary pl-5 py-1">
            <p>
              Mechanical Bull Shop respects the privacy of people who visit our
              website, submit enquiries and interact with our products and
              services. This Privacy Policy explains the types of information we
              may collect through MechanicalBullShop.com, why we use it and the
              choices available to you.
            </p>
            <p>
              This policy applies to information collected through our website,
              including general contact forms, product enquiries, quote
              requests and other website interactions.
            </p>
          </div>
        </header>

        <Separator className="my-10" />

        {/* Content Body */}
        <div className="space-y-12 leading-relaxed text-foreground/90 text-base">
          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Information We Collect
            </h2>
            <p>
              Depending on how you use our website, we may receive information
              that you voluntarily provide to us, such as:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2 text-sm">
              {["Name", "Email address", "Phone number", "Company or venue name", "Product interests", "Product enquiry details", "Shipping or delivery location supplied in an order enquiry", "Messages and questions", "Purchase preference, such as full-payment or deposit intent"].map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 bg-muted/50 p-2.5 border border-border/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <span>{item}</span>
                </li>)}
            </ul>
            <div className="p-4 bg-muted border border-border text-sm text-foreground">
              <strong>Notice regarding payment data:</strong> The current
              Request a Quote form records the buyer&apos;s selected payment
              preference but <strong>does not itself process a payment</strong>.
            </div>
          </section>

          <Separator />

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Information Collected Automatically
            </h2>
            <p>
              When you browse our website, standard web server technologies may
              automatically collect technical data necessary for site
              functionality and security, including:
            </p>
            <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm text-muted-foreground">
              <li>IP address</li>
              <li>Browser type and device characteristics</li>
              <li>Referring website addresses</li>
              <li>Pages viewed and navigation paths on our site</li>
              <li>Basic server and security access logs</li>
              <li>Cookies where enabled by your browser</li>
            </ul>
          </section>

          <Separator />

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              How We Use Information
            </h2>
            <p>
              We use the collected information strictly for operational and
              customer service purposes, including to:
            </p>
            <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm text-muted-foreground">
              <li>Respond to direct questions and communications</li>
              <li>Respond to commercial equipment and product enquiries</li>
              <li>Prepare customized quotes and formal order paperwork</li>
              <li>Identify the specific bull models or parts of interest</li>
              <li>Review delivery specifications and freight logistics</li>
              <li>Communicate updates regarding an enquiry or purchase</li>
              <li>Maintain, monitor, and secure the website infrastructure</li>
              <li>Prevent spam, unauthorized activity, abuse, or fraud</li>
              <li>Improve site usability and overall customer experience</li>
              <li>Comply with applicable commercial and legal obligations</li>
            </ul>
          </section>

          <Separator />

          {/* Section 4 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Product Enquiries and Quote Requests
            </h2>
            <p>
              When you submit a product enquiry or Request a Quote form, we use
              the information you provide to understand the product you are
              considering, your selected purchase preference, delivery
              information and any questions you submit.
            </p>
            <p className="font-medium text-foreground">
              Submitting an enquiry does not by itself complete a purchase or
              process a payment.
            </p>
          </section>

          <Separator />

          {/* Section 5 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              WooCommerce and Website Product Data
            </h2>
            <p>
              We utilize WooCommerce as our website product catalog management
              system to maintain accurate specifications, inventory listings,
              and pricing. Specifically, it manages:
            </p>
            <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm text-muted-foreground">
              <li>Products and package configurations</li>
              <li>Base pricing and optional accessories</li>
              <li>Availability status</li>
              <li>Product categories and classifications</li>
              <li>Equipment specification imagery</li>
              <li>SKUs and internal product references</li>
            </ul>
            <p className="text-sm text-muted-foreground pt-1">
              WooCommerce does not process payments for complete mechanical bull
              purchases on this site, consistent with our commercial quote-based
              sales model.
            </p>
          </section>

          <Separator />

          {/* Section 6 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Cookies
            </h2>
            <p>
              Cookies are small data files stored on your browser when visiting
              websites. We may use essential cookies to support basic site
              navigation, session integrity, security defenses, and basic user
              preferences.
            </p>
            <p className="text-sm text-muted-foreground">
              You can instruct your browser to refuse all cookies or to indicate
              when a cookie is being sent. However, disabling technical cookies
              may affect the proper functioning of certain parts of the site.
            </p>
          </section>

          <Separator />

          {/* Section 7 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              How We Share Information
            </h2>
            <p className="font-semibold text-foreground">
              We do not sell customer contact information.
            </p>
            <p>
              We share information with service providers only where needed to
              operate the website, communicate with customers, prevent fraud,
              provide hosting and email infrastructure, or support an order.
            </p>
            <p className="text-sm text-muted-foreground">
              When an order actually proceeds, necessary delivery and contact
              details will be shared with commercial freight or logistics
              providers to arrange freight delivery and dispatch.
            </p>
          </section>

          <Separator />

          {/* Section 8 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Data Security
            </h2>
            <p>
              We maintain reasonable technical and organizational measures to
              protect information submitted through our site against unauthorized
              access, alteration, disclosure, or destruction.
            </p>
            <p className="text-sm text-muted-foreground">
              Please note that no method of transmission over the internet or
              method of electronic storage is completely secure, and we cannot
              guarantee absolute security.
            </p>
          </section>

          <Separator />

          {/* Section 9 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Data Retention
            </h2>
            <p>
              Information is retained only for as long as reasonably necessary
              to fulfill the purposes for which it was gathered, maintain
              standard commercial business records, resolve potential disputes,
              and comply with applicable legal obligations.
            </p>
          </section>

          <Separator />

          {/* Section 10 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Your Privacy Choices
            </h2>
            <p>
              Depending on your location, you may have rights under applicable
              privacy laws to request access to, correction of, or deletion of
              personal information you have previously provided to us.
            </p>
            <p>
              To exercise these choices or inquire about data submitted through
              our forms, please contact us at:
            </p>
            <p className="font-mono text-sm">
              <a href={`mailto:${contactEmail}`} className="text-primary hover:underline font-semibold">
                {contactEmail}
              </a>
            </p>
          </section>

          <Separator />

          {/* Section 11 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Children&apos;s Privacy
            </h2>
            <p>
              Our website, commercial equipment, and related services are
              strictly intended for adult business purchasers and operators. We
              do not knowingly collect or solicit personal information from
              children under the age of 18.
            </p>
          </section>

          <Separator />

          {/* Section 12 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Third-Party Links
            </h2>
            <p>
              Our website may occasionally contain links to external sites or
              resources. We do not control and are not responsible for the
              content, security practices, or privacy policies of any third-party
              websites.
            </p>
          </section>

          <Separator />

          {/* Section 13 */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight text-foreground uppercase">
              Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes
              in our operations or regulatory requirements. The date displayed
              at the top of this page indicates when the latest revisions were
              published.
            </p>
          </section>

          <Separator />

          {/* Section 14 */}
          <section className="space-y-4 bg-muted/60 p-6 md:p-8 border border-border">
            <div className="flex items-center gap-2.5 text-foreground font-serif font-bold uppercase text-lg sm:text-xl">
              <Mail className="size-5 text-primary" />
              <h2>Contact Us About Privacy</h2>
            </div>
            <p className="text-sm md:text-base text-foreground/90">
              Questions about this Privacy Policy or personal information
              submitted through the website can be sent to:
            </p>
            <div className="pt-2">
              <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-2 text-base font-mono font-semibold text-primary hover:underline">
                <FileText className="size-4" />
                <span>{contactEmail}</span>
              </a>
            </div>
          </section>
        </div>
      </div>
    </section>;
}